import { useRef, useEffect, useState, ComponentPropsWithoutRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  RadioGroup,
  Textarea,
  Input,
  InputProps,
  Modal,
  Button,
  InputField,
} from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import { closeAndResetModal } from '../ModalsSlice';
import { createObject, importFile } from 'Storage/pages/StoragePage/StoragePageSlice';

import Section from './Section/Section';

import { InteractionController, PageLayoutField } from '_common/components';
import { completeAction, setPulseData } from 'App/redux/onboardingSlice';
import { useOnboardingExplorerActive } from '_common/hooks/Onboarding';
import styles from './NewFolderDocumentModal.module.scss';

type CreateObjectParams = Parameters<typeof createObject>[0]['parameters'];

type TypeOfObject<T extends ObjectType> = Record<Uppercase<T>, T>;

export const NEW_TYPES: TypeOfObject<'folder'> & TypeOfObject<'document'> & TypeOfObject<'file'> = {
  FOLDER: 'folder',
  DOCUMENT: 'document',
  FILE: 'file',
};

const MODAL = 'NewFolderDocumentModal';

const NewFolderDocumentModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const isOpen = useSelector((state) => state.modals.open[MODAL]);
  const newType = useSelector((state) => state.modals[MODAL].newType);
  const fileRedux = useSelector((state) => state.modals[MODAL].file);
  const current = useSelector((state) => state.modals[MODAL].current);
  const id = useSelector((state) => state.modals[MODAL].id);
  const identity = useSelector((state) => state.modals[MODAL].identity);
  const actionsCompleted = useSelector((state) => state.onboarding.actionsCompleted);
  const onboardingExplorerIsActive = useOnboardingExplorerActive();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [validations, setValidations] = useState<{ name?: string; description?: string }>({
    name: '',
    description: '',
  });
  const [file, setFile] = useState<File | undefined>(undefined);
  const [pageSize, setPageSize] = useState<PageSize>('a4');
  const [pageOrientation, setPageOrientation] = useState<PageOrientation>('portrait');
  const inputRef = useRef<HTMLInputElement>(null);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const newContentInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setValidations({});
      setDescription('');
      setFile(fileRedux);
      setName((fileRedux && fileRedux.name && removeNameExtension(fileRedux.name)) || '');

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  }, [isOpen, fileRedux]);

  useEffect(() => {
    if (!name) {
      setTimeout(() => {
        const current = inputRef?.current;
        if (current) {
          const rect = current?.getBoundingClientRect();
          dispatch(
            setPulseData({
              documentInputRect: {
                top: rect?.top,
                left: rect?.left,
                height: rect?.height,
                width: rect?.width,
              },
            }),
          );
        } else {
          dispatch(
            setPulseData({
              documentButtonRect: undefined,
              documentInputRect: undefined,
            }),
          );
        }
      }, 0);
    } else {
      if (ctaRef?.current) {
        const rect = ctaRef.current?.getBoundingClientRect();
        dispatch(
          setPulseData({
            documentButtonRect: {
              top: rect?.top,
              left: rect?.left,
              height: rect?.height,
              width: rect?.width,
            },
            documentInputRect: undefined,
          }),
        );
      }
    }

    return () => {
      dispatch(
        setPulseData({
          documentInputRect: undefined,
        }),
      );
    };
  }, [name, actionsCompleted]);

  const removeNameExtension = (name: File['name']) => {
    if (!name) {
      return '';
    }
    return name.substring(0, name.lastIndexOf('.'));
  };

  const onNameChanged: InputProps['onChange'] = (e) => {
    const valids: typeof validations = {};

    const inputName = e.target.value;
    setName(inputName);

    if (new RegExp(/[<>/:?*"\\|]/).test(inputName)) {
      valids.name = intl.formatMessage(
        {
          id: 'UNSUPPORTED_CHARACTER_MESSAGE',
        },
        { type: newType },
      );
    } else if (inputName.length === 0) {
      valids.name = intl.formatMessage({
        id: 'validation.name.insertName',
      });
    } else if (newType === 'document' && inputName.length > 146) {
      valids.name = intl.formatMessage({ id: 'validation.name.longName' }, { length: 146 });
    } else if (inputName.length > 150) {
      valids.name = intl.formatMessage({ id: 'validation.name.longName' }, { length: 150 });
    } else {
      valids.name = '';
    }

    if (Object.keys(valids).length > 0) {
      setValidations(valids);
    }
  };

  const onDescriptionChanged: ComponentPropsWithoutRef<typeof Textarea>['onChange'] = (e) => {
    const valids: typeof validations = {};
    const target = e.target as HTMLTextAreaElement;
    const inputDescription = target.value;
    setDescription(inputDescription);
    if (inputDescription.length > 1000) {
      valids.description = intl.formatMessage({
        id: 'validation.description.longDescription',
      });
    } else {
      valids.description = '';
    }

    if (Object.keys(valids).length > 0) {
      setValidations(valids);
    }
  };

  const getTranslations = () => {
    switch (newType) {
      case NEW_TYPES.FOLDER:
        return {
          title: intl.formatMessage({
            id: 'storage.modals.createFolder.title',
          }),
          inputTitle: intl.formatMessage({
            id: 'NEW_FOLDER_TITLE',
          }),
          inputPlaceholder: intl.formatMessage({
            id: 'NEW_FOLDER_PLACEHOLDER',
          }),
          description: intl.formatMessage({
            id: 'NEW_FOLDER_DESCRIPTION',
          }),
          descriptionPlaceholder: intl.formatMessage({
            id: 'NEW_FOLDER_DESCRIPTION_PLACEHOLDER',
          }),
          createButton: 'storage.modals.createFolder.createButton',
        };
      case NEW_TYPES.DOCUMENT:
        return {
          title: intl.formatMessage({
            id: 'storage.modals.createDocument.title',
          }),
          inputTitle: intl.formatMessage({
            id: 'NEW_DOCUMENT_TITLE',
          }),
          inputPlaceholder: intl.formatMessage({
            id: 'NEW_DOCUMENT_PLACEHOLDER',
          }),
          description: intl.formatMessage({
            id: 'NEW_DOCUMENT_DESCRIPTION',
          }),
          descriptionPlaceholder: intl.formatMessage({
            id: 'NEW_DOCUMENT_DESCRIPTION_PLACEHOLDER',
          }),
          createButton: 'storage.modals.createDocument.createButton',

          importContentFrom: intl.formatMessage({
            id: 'storage.modals.createDocument.importContentFrom',
          }),
          importOtherContent: 'storage.modals.createDocument.importOtherContent',
        };
      case NEW_TYPES.FILE:
        return {
          title: intl.formatMessage({
            id: 'storage.modals.createFile.title',
          }),
          inputTitle: intl.formatMessage({
            id: 'storage.modals.createDocument.titleInputTitle',
          }),
          description: intl.formatMessage({
            id: 'storage.modals.createFolder.description',
          }),
          createButton: 'storage.modals.createFile.createButton',
        };
      default:
        return {};
    }
  };

  const handleOnClickNew = () => {
    handleNewFolderDocument(newType, {
      name,
      description,
      id,
      file,
    });
    dispatch(completeAction('explorer_my_files_document_created'));
  };

  const handleOnChangeImportContent = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setDescription('');
    setFile(file);
    setName(removeNameExtension(file?.name || ''));
  };

  const close = () => {
    dispatch(closeAndResetModal(MODAL));
  };

  const renderTemplateFileElement = () => {
    const translations = getTranslations();

    if (file && file.name) {
      return (
        <div style={{ display: 'flex', flexDirection: 'row', marginTop: '2rem' }}>
          <span style={{ paddingRight: '1rem' }}>{translations.importContentFrom}</span>
          <b>{file.name}</b>
        </div>
      );
    }

    return null;
  };

  const handleNewFolderDocument = (
    type: (typeof NEW_TYPES)[keyof typeof NEW_TYPES],
    values: Pick<CreateObjectParams, 'name' | 'description'> & {
      id: CreateObjectParams['parent'];
      file?: File;
    },
  ) => {
    const currentType = current.type;
    const parameters: CreateObjectParams & Pick<typeof values, 'file'> = {
      name: '',
      description: '',
    };

    if (currentType === 'space') {
      parameters.name = values.name;
      parameters.description = values.description;
      parameters.space = values.id;
    } else {
      parameters.name = values.name;
      parameters.description = values.description;
      parameters.parent = values.id;
    }

    switch (type) {
      case NEW_TYPES.FOLDER: {
        dispatch(createObject({ identity, parameters, type: 'folder' }));
        break;
      }

      case NEW_TYPES.DOCUMENT: {
        // TODO: verify if is an imported a file
        if (values.file && values.file.name) {
          parameters.file = values.file;
          dispatch(
            importFile({
              identity,
              parameters: { ...parameters, file: parameters.file },
              objectType: 'document',
            }),
          );
        } else {
          parameters.pageSize = pageSize;
          parameters.pageOrientation = pageOrientation;
          dispatch(createObject({ identity, parameters, type: 'document' }));
        }
        break;
      }

      case NEW_TYPES.FILE: {
        dispatch(createObject({ identity, parameters, type: 'file' }));
        break;
      }

      default:
        break;
    }

    close();
  };

  const translations = getTranslations();

  return (
    <Modal
      width="75rem"
      open={!!isOpen}
      close={onboardingExplorerIsActive ? () => {} : close}
      testId="create-object"
    >
      <div id="create-object-modal" className="flex min-h-0 flex-col">
        <InteractionController
          environment="explorer"
          style={{ width: '100%', flexDirection: 'column' }}
        >
          <Modal.Header>{translations.title}</Modal.Header>
        </InteractionController>
        <Modal.Body>
          <Section
            title={
              newType === NEW_TYPES.DOCUMENT && !file?.name
                ? intl.formatMessage({ id: 'DOCUMENT_GENERAL_INFORMATION' })
                : undefined
            }
          >
            <InteractionController
              environment="explorer"
              rules={[{ interaction: 'explorer_document_create' }]}
              style={{ width: '100%', flexDirection: 'column' }}
            >
              <InputField
                label={translations.inputTitle}
                size="l"
                feedbackContent={validations.name}
                testId="create-object-modal-title"
              >
                <Input
                  id="storage.modals.create.input"
                  ref={inputRef}
                  size="l"
                  value={name}
                  onChange={onNameChanged}
                  placeholder={translations.inputPlaceholder ?? ''}
                  hasError={!!validations.name}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && validations.name === '') {
                      e.preventDefault();
                      handleOnClickNew();
                    }
                  }}
                  testId="create-object-modal-title"
                />
              </InputField>
            </InteractionController>
            <InputField
              label={translations.description}
              feedbackContent={validations.description}
              size="l"
              testId="create-object-modal-description-textarea"
            >
              <InteractionController
                environment="explorer"
                style={{ width: '100%', flexDirection: 'column' }}
              >
                <Textarea
                  plainText
                  name="description"
                  type="text"
                  size="m"
                  placeholder={translations.descriptionPlaceholder}
                  initialContent={description}
                  onChange={onDescriptionChanged}
                  hasError={!!validations.description}
                  testId="create-object-modal-description-textarea"
                />
              </InteractionController>
            </InputField>
          </Section>
          <InteractionController
            environment="explorer"
            style={{ width: '100%', flexDirection: 'column' }}
          >
            {newType === NEW_TYPES.DOCUMENT && !file?.name && (
              <Section title={intl.formatMessage({ id: 'DOCUMENT_PAGES' })} margin="1rem 0 0 0">
                <PageLayoutField
                  size="l"
                  type="size"
                  testId={{
                    inputField: 'create-object-modal-page-size-field',
                    select: 'create-object-modal-page-size-select',
                  }}
                  onChange={(option) => {
                    setPageSize(option?.value as PageSize);
                  }}
                />
                <InputField
                  size="l"
                  label={intl.formatMessage({ id: 'PAGE_ORIENTATION' })}
                  testId="create-object-modal-page-orientation"
                >
                  <RadioGroup
                    items={[
                      { value: 'portrait', label: intl.formatMessage({ id: 'PORTRAIT' }) },
                      { value: 'landscape', label: intl.formatMessage({ id: 'LANDSCAPE' }) },
                    ]}
                    value={pageOrientation}
                    onChange={(e) => setPageOrientation(e.target.value)}
                    direction="vertical"
                    size="xs"
                    testId="create-object-modal-page-orientation"
                  />
                </InputField>
              </Section>
            )}
            {renderTemplateFileElement()}
          </InteractionController>
        </Modal.Body>
        <Modal.Footer
          style={{
            justifyContent: file?.name ? 'space-between' : 'flex-end',
            alignItems: 'center',
          }}
        >
          {file && file.name && (
            <div className={styles.button}>
              <Button
                size="m"
                variant="standard"
                style={{ position: 'relative' }}
                testId="create-object-import"
                onClick={() => newContentInputRef.current?.click()}
              >
                <FormattedMessage id={translations.importOtherContent} />
              </Button>
              <input
                ref={newContentInputRef}
                type="file"
                accept=".docx, .html, .htm"
                style={{
                  display: 'none',
                }}
                onChange={handleOnChangeImportContent}
              />
            </div>
          )}
          <InteractionController
            environment="explorer"
            rules={[{ interaction: 'explorer_document_create' }]}
          >
            <Button
              size="m"
              className={styles.button}
              variant="primary"
              onClick={handleOnClickNew}
              disabled={!!validations.name || !!validations.description || name === ''}
              testId="create-object-submit"
              ref={ctaRef}
            >
              <FormattedMessage id={translations.createButton} />
            </Button>
          </InteractionController>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default NewFolderDocumentModal;
